/**
 * socket-client shim — replaces prod lib/socket-client.ts. One singleton with TWO
 * transports, switched at RUNTIME:
 *
 *   • LOOPBACK (default, always): same-tab delivery. No peer, no server, no network
 *     — the therapist-side broadcast reaches the same tab's mirror listeners so the
 *     Therapist⇄Client toggle works offline. This is what every ordinary demo
 *     visitor gets, in BOTH the static and the live build.
 *
 *   • RELAY (live builds only, and ONLY after enableRelay() — called when a
 *     co-driven room starts at /live): a thin adapter over the browser-native
 *     WebSocket speaking the relay's tiny JSON {event,payload} protocol. Connects
 *     ONLY to VITE_RELAY_URL — never prod. Keeping the swap behind an explicit
 *     runtime switch means plain visitors NEVER open a connection, and two
 *     strangers browsing the demo can never share a room (the vendored BLS hooks
 *     join a constant "demo-session" code, which would otherwise collide).
 *
 * The handler map lives in the WRAPPER, not the transport, so components that
 * subscribed before the swap keep working after it. `socket.io-client` is never
 * used (the isolation guard forbids it).
 */

type Handler = (payload: any) => void;

export interface DemoSocket {
  id: string;
  connected: boolean;
  on(event: string, fn: Handler): DemoSocket;
  off(event: string, fn: Handler): DemoSocket;
  emit(event: string, payload?: unknown): DemoSocket;
  connect(): DemoSocket;
  disconnect(): DemoSocket;
}

const LIVE = import.meta.env.VITE_DEMO_LIVE === "1";
const RELAY_URL = (import.meta.env.VITE_RELAY_URL as string | undefined) ?? "";

// ── Singleton state ───────────────────────────────────────────────────────────
const handlers = new Map<string, Set<Handler>>();
let mode: "loopback" | "relay" = "loopback";

// Room membership (sessionCode -> join frame), tracked in EVERY mode so that the
// moment the relay comes up (or reconnects) we can replay every current `join` —
// a connection can be in several rooms at once (live room + a BLS session room).
const joined = new Map<string, string>();

// Relay transport internals (created lazily by enableRelay()).
let ws: WebSocket | null = null;
let outbox: string[] = [];
let closedByUser = false;
let backoff = 500;

function dispatch(event: string, payload: unknown): void {
  const set = handlers.get(event);
  if (set) for (const fn of [...set]) fn(payload);
}

// Same-tab "mirror" events: the therapist⇄client BLS mirror (use-bilateral-mirror)
// listens for these IN THE SAME TAB. In relay mode we must STILL loopback-dispatch
// them locally (so the driver's own Client-view toggle works) AND relay to the peer.
// Peer-coordination events (demo_action/nav/control, join/leave, rtc_*) are NOT here:
// loopback-dispatching demo_action would re-enter DemoStateProvider's listener and
// double-apply every reducer action (the live-build solo-visitor duplicate-client bug).
const MIRROR_LOOPBACK = new Set<string>([
  "frame",
  "session_start",
  "session_end",
  "session_saved",
  "checkin_state",
  "checkin_data",
  "checkin_complete",
]);

// Events that stay on THIS screen even while relaying. `frame` is the BLS ball stream
// (~20fps + a 2s heartbeat) feeding the same-tab Client-view mirror. In a co-driven
// room both consoles are already identical through synced keys (useSyncedState), so
// each screen's mirror follows its own console. Relaying BOTH screens' frames would make
// each mirror flicker between two ball phases, and a stale heartbeat could snap a slider
// back mid-drag.
const LOCAL_ONLY = new Set<string>(["frame"]);

function wsSend(frame: string): void {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(frame);
  else outbox.push(frame);
}

function openRelay(): void {
  if (closedByUser) return;
  ws = new WebSocket(`${RELAY_URL}/rtc`);
  ws.onopen = () => {
    backoff = 500;
    socket.connected = true;
    for (const frame of joined.values()) ws!.send(frame); // re-enter EVERY room first
    const pending = outbox;
    outbox = [];
    for (const frame of pending) ws!.send(frame);
  };
  ws.onmessage = (ev) => {
    let frame: { event?: string; payload?: unknown };
    try {
      frame = JSON.parse(typeof ev.data === "string" ? ev.data : "");
    } catch {
      return;
    }
    if (frame && typeof frame.event === "string") dispatch(frame.event, frame.payload);
  };
  const reconnect = () => {
    socket.connected = false;
    ws = null;
    if (closedByUser || mode !== "relay") return;
    setTimeout(openRelay, backoff);
    backoff = Math.min(backoff * 2, 10_000);
  };
  ws.onclose = reconnect;
  ws.onerror = () => ws?.close();
}

/**
 * Switch the singleton from loopback to the relay transport. Called by
 * LiveRoomProvider when a co-driven room starts. Idempotent; a no-op in static
 * builds or when no relay URL is configured. Existing listeners keep working —
 * only delivery changes (peer-relayed instead of same-tab).
 */
export function enableRelay(): void {
  if (!LIVE || !RELAY_URL || mode === "relay") return;
  mode = "relay";
  closedByUser = false;
  openRelay();
}

/**
 * Switch BACK to loopback (called by endRoom). Without this, a guest who ends the
 * walkthrough and keeps exploring the demo solo would have a permanently broken
 * session mirror + check-in flow (relay-mode frames going to an empty room). Closes
 * the socket and forgets room membership so a fresh room later re-joins cleanly.
 */
export function disableRelay(): void {
  mode = "loopback";
  closedByUser = true;
  ws?.close();
  ws = null;
  outbox = [];
  joined.clear();
  socket.connected = true; // loopback is always "connected"
}

/** True while the relay transport is active (a live co-driven room is running). */
export function isRelayActive(): boolean {
  return mode === "relay";
}

const socket: DemoSocket = {
  id: LIVE ? "demo-switchable" : "demo-local",
  connected: true, // loopback is always "connected"; relay updates this live
  on(event, fn) {
    let set = handlers.get(event);
    if (!set) handlers.set(event, (set = new Set()));
    set.add(fn);
    return socket;
  },
  off(event, fn) {
    handlers.get(event)?.delete(fn);
    return socket;
  },
  emit(event, payload) {
    const frame = JSON.stringify({ event, payload });
    // Track membership in every mode so a later transport swap/reconnect replays it.
    // host_join counts: a reconnecting HOST must re-open its room (with the key) or
    // the relay would refuse the guest's rejoin.
    const code = (payload as { sessionCode?: string } | undefined)?.sessionCode;
    if ((event === "join" || event === "host_join") && typeof code === "string") joined.set(code, frame);
    if (event === "leave" && typeof code === "string") joined.delete(code);

    const loopbackDispatch = () => {
      // Async to mimic a transport hop and avoid synchronous re-entrancy in render.
      const set = handlers.get(event);
      if (set && set.size > 0) queueMicrotask(() => { for (const fn of set) fn(payload); });
    };
    if (mode === "relay") {
      if (!LOCAL_ONLY.has(event)) wsSend(frame);
      // Mirror-class events also fire locally so the same-tab BLS mirror keeps working
      // while relaying to the peer. (Peer-coordination events do NOT — see MIRROR_LOOPBACK.)
      if (MIRROR_LOOPBACK.has(event)) loopbackDispatch();
    } else {
      loopbackDispatch(); // loopback: everything is same-tab
    }
    return socket;
  },
  connect() {
    closedByUser = false;
    if (mode === "relay" && !ws) openRelay();
    return socket;
  },
  disconnect() {
    closedByUser = true;
    ws?.close();
    ws = null;
    return socket;
  },
};

export function getSocket(): DemoSocket {
  return socket;
}
